import { useState } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import ScrollReveal from "@/components/base/ScrollReveal";

type FaqItem = {
  question: string;
  answer: string;
};

type FaqCategory = {
  id: string;
  label: string;
  items: FaqItem[];
};

const faqData: FaqCategory[] = [
  {
    id: "policy",
    label: "保單門神",
    items: [
      {
        question: "什麼是保單健診？",
        answer:
          "保單健診是把你現有的保單整理成一張保障地圖，檢查是否有重複、缺口、保費過高或保障不足的狀況，協助你知道哪些該保留、哪些可調整、哪些需要補強。",
      },
      {
        question: "我已經有很多保單，還需要健診嗎？",
        answer:
          "越多保單越需要整理。很多人不是沒有保險，而是不知道保障是否重複、是否符合現在的家庭責任與醫療風險。",
      },
      {
        question: "保單健診一定要重新買保險嗎？",
        answer:
          "不一定。保單門神的重點是先釐清，不是直接銷售。會先看現有保單是否可保留與調整，再依照缺口討論是否需要補強。",
      },
    ],
  },
  {
    id: "asset",
    label: "財產門神",
    items: [
      {
        question: "財產門神是在做投資建議嗎？",
        answer:
          "財產門神的重點是資產配置與風險控管，不是單純追逐高報酬。會先協助你釐清現金流、預備金、保費、投資比例與風險承受度。",
      },
      {
        question: "為什麼資產配置需要搭配保險？",
        answer:
          "因為沒有保障的資產配置，容易被一次重大風險打斷。保險是風險底層防線，資產配置則是中長期成長與現金流安排。",
      },
      {
        question: "市場波動時該怎麼辦？",
        answer:
          "若事前有做好資金分艙與再平衡規則，市場波動時就不需要只靠情緒決策，而是依照原本設定的紀律調整。",
      },
    ],
  },
  {
    id: "life",
    label: "人生門神",
    items: [
      {
        question: "教育金與退休金可以一起規劃嗎？",
        answer:
          "可以，而且建議一起看。因為家庭資源有限，若只顧教育金而忽略退休金，未來可能造成另一種壓力。",
      },
      {
        question: "什麼時候需要開始做退休規劃？",
        answer:
          "越早越好。退休規劃不是等快退休才開始，而是提早建立現金流、保障、醫療與長照緩衝區。",
      },
      {
        question: "保險信託適合什麼樣的人？",
        answer:
          "適合重視資產傳承、照護安排、保險金流向與家族共識的人。若家庭成員多、資產分配複雜，更需要提前規劃。",
      },
    ],
  },
  {
    id: "enterprise",
    label: "企業門神",
    items: [
      {
        question: "企業門神適合什麼公司？",
        answer:
          "適合中小企業主、負責人、合夥人、人資主管，以及想要檢視薪資結構、勞動合約、員工福利、企業主保障與傳承架構的企業。",
      },
      {
        question: "企業門神與一般保險業務有何不同？",
        answer:
          "企業門神不是只看團險或保單，而是從企業主風險、人事制度、薪資結構、法遵風險、員工福利與企業傳承做整合檢視。",
      },
      {
        question: "企業諮詢會不會很複雜？",
        answer:
          "會先從現況盤點開始，不會一次要求企業大幅改變。重點是找出高風險項目，依優先順序逐步優化。",
      },
    ],
  },
];

export default function Faq() {
  const [openKey, setOpenKey] = useState<string>("policy-0");

  const toggle = (key: string) => {
    setOpenKey(openKey === key ? "" : key);
  };

  return (
    <div className="min-h-screen bg-brand-black">
      <SEOHead
        title="常見問答 FAQ | 保障・資產・人生・企業規劃 | 保險門神"
        description="保險門神常見問答，涵蓋保單健診、資產配置、教育金與退休規劃、企業風險管理等四大領域的常見問題，一次解答你的保險與理財疑惑。"
        keywords="保險FAQ,保單健診問題,資產配置問答,退休規劃常見問題,企業保險諮詢"
        canonicalPath="/faq"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "name": "保險門神常見問答",
          "description": "涵蓋保單健診、資產配置、教育金與退休規劃、企業風險管理等四大領域的常見問題。",
          "url": "__SITE_URL__/faq",
          "mainEntity": faqData.flatMap((cat) =>
            cat.items.map((item) => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          ),
          "isPartOf": {
            "@type": "WebSite",
            "name": "保險門神 Insurance Guardian",
            "url": "__SITE_URL__/"
          }
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20question%20marks%20and%20knowledge%20symbols%20floating%20on%20deep%20black%20canvas%2C%20subtle%20light%20rays%20emanating%20from%20central%20golden%20glow%20representing%20clarity%20and%20answers%2C%20premium%20consulting%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%20with%20warm%20golden%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=600&seq=205&orientation=landscape"
              alt="常見問答背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          <div className="container-main relative z-10 text-center">
            <div className="inline-block border border-brand-border rounded-full px-5 py-2 mb-6">
              <span className="text-brand-gold text-[1rem] font-medium tracking-wide">
                FAQ
              </span>
            </div>
            <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-4">
              常見問答 FAQ
            </h1>
            <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] max-w-[640px] mx-auto">
              把你最在意的保障、資產、人生規劃與企業風險，一次說清楚。
            </p>
            <div className="w-[60px] h-[2px] bg-brand-gold mx-auto mt-4" />
          </div>
        </section>

        {/* FAQ Accordion */}
        <section className="pb-20 md:pb-28">
          <div className="container-main max-w-[860px]">
            <div className="space-y-12 md:space-y-14">
              {faqData.map((cat, catIndex) => (
                <ScrollReveal key={cat.id} delay={catIndex * 100}>
                  <div>
                    {/* Category Label */}
                    <div className="flex items-center gap-3 mb-6 md:mb-8">
                      <div className="w-1 h-8 bg-brand-gold rounded-full" />
                      <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                        {cat.label}
                      </h2>
                    </div>

                    {/* Accordion Items */}
                    <div className="space-y-4">
                      {cat.items.map((item, idx) => {
                        const key = `${cat.id}-${idx}`;
                        const isOpen = openKey === key;

                        return (
                          <div
                            key={key}
                            className={`bg-brand-card border rounded-xl overflow-hidden transition-all duration-300 ${
                              isOpen
                                ? "border-brand-gold/60"
                                : "border-brand-border hover:border-brand-gold/40"
                            }`}
                          >
                            <button
                              onClick={() => toggle(key)}
                              className="w-full flex items-start justify-between gap-4 p-5 md:p-6 text-left"
                            >
                              <div className="flex items-start gap-3 md:gap-4">
                                <span
                                  className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full text-[0.875rem] md:text-[1rem] font-bold shrink-0 mt-0.5 ${
                                    isOpen
                                      ? "bg-brand-gold text-brand-black"
                                      : "bg-brand-gold/15 text-brand-gold"
                                  }`}
                                >
                                  Q{idx + 1}
                                </span>
                                <span className="text-white text-[1.0625rem] md:text-[1.25rem] font-medium leading-[1.55] pt-1">
                                  {item.question}
                                </span>
                              </div>
                              <span
                                className={`w-6 h-6 flex items-center justify-center text-brand-gold shrink-0 mt-1 transition-transform duration-300 ${
                                  isOpen ? "rotate-180" : ""
                                }`}
                              >
                                <i className="ri-arrow-down-s-line text-xl" />
                              </span>
                            </button>

                            <div
                              className={`overflow-hidden transition-all duration-300 ${
                                isOpen ? "max-h-[400px]" : "max-h-0"
                              }`}
                            >
                              <div className="px-5 md:px-6 pb-5 md:pb-6 pl-16 md:pl-[4.25rem]">
                                <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.75]">
                                  {item.answer}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Conversion: Still can't find your answer? */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20dialogue%20bubbles%20and%20communication%20light%20trails%20on%20deep%20black%20canvas%2C%20subtle%20warm%20golden%20glow%20connecting%20two%20points%20representing%20human%20conversation%20and%20guidance%2C%20premium%20consulting%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%20with%20soft%20golden%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=600&seq=208&orientation=landscape"
              alt="還找不到答案背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/80" />
          </div>

          <div className="container-main relative z-10">
            <ScrollReveal className="max-w-[760px] mx-auto text-center mb-10 md:mb-14">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-brand-gold/15 mx-auto mb-6">
                <span className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-brand-gold text-2xl md:text-3xl">
                  <i className="ri-question-answer-line" />
                </span>
              </div>
              <h2 className="text-white font-display font-bold text-[1.75rem] md:text-[2.5rem] leading-[1.3] mb-4">
                還找不到你的答案？
              </h2>
              <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75]">
                每個家庭的狀況都不一樣，讓我們針對你的實際需求，給你一對一的專業建議。
              </p>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-[860px] mx-auto">
              {/* LINE Card */}
              <ScrollReveal delay={100}>
                <div className="bg-brand-black/80 border border-brand-border rounded-2xl p-7 md:p-10 text-center transition-all duration-300 hover:border-brand-gold/60 h-full">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-brand-gold/10 mx-auto mb-5">
                    <span className="w-7 h-7 flex items-center justify-center text-brand-gold text-2xl">
                      <i className="ri-line-fill" />
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.5rem] mb-3">
                    快速加入 LINE
                  </h3>
                  <p className="text-brand-text-secondary text-[1.0625rem] leading-[1.7] mb-6">
                    留下你的問題與需求，我們會在 24 小時內回覆你。
                  </p>
                  <a
                    href="https://lin.ee/o51JBHX"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-8 py-3.5 rounded-full text-[1.0625rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
                  >
                    <i className="ri-line-fill" />
                    加入 LINE 諮詢
                  </a>
                </div>
              </ScrollReveal>

              {/* Form Card */}
              <ScrollReveal delay={200}>
                <div className="bg-brand-black/80 border border-brand-border rounded-2xl p-7 md:p-10 text-center transition-all duration-300 hover:border-brand-gold/60 h-full">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-brand-gold/10 mx-auto mb-5">
                    <span className="w-7 h-7 flex items-center justify-center text-brand-gold text-2xl">
                      <i className="ri-mail-send-line" />
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.5rem] mb-3">
                    填寫諮詢表單
                  </h3>
                  <p className="text-brand-text-secondary text-[1.0625rem] leading-[1.7] mb-6">
                    完整描述你的情況，我們會幫你安排最適合的顧問與時間。
                  </p>
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 border border-brand-border text-white px-8 py-3.5 rounded-full text-[1.0625rem] font-semibold hover:border-brand-gold hover:text-brand-gold transition-colors whitespace-nowrap"
                  >
                    <i className="ri-edit-line" />
                    填寫諮詢表單
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Legacy CTA removed — replaced by the richer conversion block above */}
      </main>
      <Footer />
    </div>
  );
}